import React from 'react';
import styled from 'styled-components';
import { NewsletterItem, formatNewsletterDate } from '../../services/newsletterApi';

/**
 * Card/Newsletter (Figma componentSet 6465:61841)
 * 588 x 440, radius 12, 이미지 위 하단 그라데이션 + 배지/타이틀/캡션 + 우상단 외부링크 버튼
 *
 * 호버 시 이미지 레이어만 scale(1.05) — 메인페이지 카드(Home.tsx:1764)와 동일 수치
 */

interface NewsletterCardProps {
  item: NewsletterItem;
}

const NewsletterCard: React.FC<NewsletterCardProps> = ({ item }) => {
  const handleClick = () => {
    if (!item.external_url) return;
    window.open(item.external_url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card onClick={handleClick}>
      <CardImage $imageUrl={item.thumbnail_url} />
      <BaseGradient />
      <Gradient />
      <Overlay>
        <CardHeader>
          <LinkButton
            type="button"
            aria-label="뉴스레터 원문 보기"
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
          >
            {/* Figma 원본 아이콘 (Shape 24x24, 40x40 버튼 안 8px 여백) */}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M18.9109 5.98953C18.9109 5.49247 18.5079 5.08953 18.0109 5.08953L8.11141 5.08951C7.61435 5.08951 7.21141 5.49245 7.21141 5.98951C7.21141 6.48656 7.61436 6.8895 8.11141 6.8895L15.8381 6.88952L5.35372 17.3739C5.00225 17.7254 5.00225 18.2952 5.35372 18.6467C5.70519 18.9982 6.27504 18.9982 6.62651 18.6467L17.1109 8.16232L17.1109 15.889C17.1109 16.3861 17.5139 16.789 18.0109 16.789C18.508 16.789 18.9109 16.3861 18.9109 15.889L18.9109 5.98953Z"
                fill="#171719"
              />
            </svg>
          </LinkButton>
        </CardHeader>

        <CardContent>
          {item.badges && item.badges.length > 0 && (
            <BadgeRow>
              {item.badges.map((badge, index) => (
                <Badge key={`${item.newsletter_id}-badge-${index}`}>{badge}</Badge>
              ))}
            </BadgeRow>
          )}

          <CardTitle>{item.title}</CardTitle>

          <Caption>
            <CaptionText>{item.author}</CaptionText>
            <Divider />
            <CaptionText>{formatNewsletterDate(item.published_at)}</CaptionText>
          </Caption>
        </CardContent>
      </Overlay>
      <CardBorder />
    </Card>
  );
};

export default NewsletterCard;

const Card = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 588 / 440;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  background-color: #f5f5f5;

  @media (max-width: 768px) {
    aspect-ratio: 4 / 3;
  }
`;

const CardImage = styled.div<{ $imageUrl: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: ${({ $imageUrl }) => `url(${$imageUrl})`};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  transition: transform 0.3s ease;

  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

/**
 * 카드 자체 fill 로 들어있는 그라데이션 (이미지 위, Gradient/Solid 아래)
 * Figma: linear-gradient(0deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 47%)
 */
const BaseGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(0deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 47%);
  pointer-events: none;
`;

/**
 * 디자인 시스템 Gradient/Solid 개발 코멘트:
 * "mask-image 및 gradient-mask으로 구현합니다. Gradient Ease: 0.25, 0.1, 0.25, 1"
 * cubic-bezier(0.25, 0.1, 0.25, 1) 곡선을 다단 스톱으로 근사했습니다.
 */
const Gradient = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 50%;
  opacity: 0.61;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 0.94) 12%,
    rgba(0, 0, 0, 0.82) 25%,
    rgba(0, 0, 0, 0.64) 40%,
    rgba(0, 0, 0, 0.42) 57%,
    rgba(0, 0, 0, 0.2) 76%,
    rgba(0, 0, 0, 0) 100%
  );
  pointer-events: none;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 32px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  align-self: stretch;
`;

const LinkButton = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: 12px;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 8px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 28px;
  padding: 0 8px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.primary};
  color: #ffffff;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 1.39em;
  letter-spacing: 0.0194em;
  white-space: nowrap;
`;

const CardTitle = styled.h3`
  margin: 0;
  color: #ffffff;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  font-size: 28px;
  line-height: 1.36em;
  letter-spacing: -0.0236em;
  white-space: pre-line;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const Caption = styled.div`
  display: flex;
  align-self: stretch;
  align-items: center;
  gap: 8px;
`;

const CaptionText = styled.span`
  color: #ffffff;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.43em;
  letter-spacing: 0.0145em;
  text-shadow: 0px 0px 1px rgba(0, 0, 0, 0.08), 0px 0px 1px rgba(0, 0, 0, 0.08),
    0px 1px 2px rgba(0, 0, 0, 0.12);
`;

/* Divider/Divider (Variant=Normal, Vertical=True) — 1x12, Semantic/Static/White + Shadow/Normal */
const Divider = styled.span`
  width: 1px;
  height: 12px;
  background: #ffffff;
  box-shadow: 0px 0px 1px 0px rgba(0, 0, 0, 0.08), 0px 0px 1px 0px rgba(0, 0, 0, 0.08),
    0px 1px 2px 0px rgba(0, 0, 0, 0.12);
  flex-shrink: 0;
`;

const CardBorder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 1px solid rgba(112, 115, 124, 0.08);
  border-radius: 12px;
  pointer-events: none;
`;
